import { useQuery, useLazyQuery } from '@apollo/client';
import { RMMClient } from '../../../index';
import { GET_MDM_CLIENT_CONFIG_FOR_CLIENT } from '../../../DeviceGraphQL/queries/MDMSettingsPage';
import { useProduct, COMMON_CONSTANTS } from '../../../index';
import {
  GetMDMClientConfigForClientVariables,
  ClientMDMConfig
} from '../../types';
import { useCallback } from 'react';

export const useMDMClientConfig = (clientId: string, skip: boolean = false) => {
  const currentProduct = useProduct();
  const isMSPProduct = currentProduct === COMMON_CONSTANTS.MSP;

  // For IT products, pass null as clientId; for MSP products, use the provided clientId
  const effectiveClientId = isMSPProduct ? clientId : null;

  // Skip the query if:
  // 1. Explicitly requested via skip parameter OR
  // 2. For MSP products when clientId is missing
  const shouldSkipQuery =
    skip || (isMSPProduct && (!clientId || clientId.trim() === ''));

  const { data, loading, error, refetch: originalRefetch } = useQuery<
    { getMDMClientConfigForClient: ClientMDMConfig },
    GetMDMClientConfigForClientVariables
  >(GET_MDM_CLIENT_CONFIG_FOR_CLIENT, {
    variables: {
      clientId: effectiveClientId
    },
    fetchPolicy: 'no-cache',
    client: RMMClient,
    skip: shouldSkipQuery
  });

  // Use lazy query to provide a working refetch function even when original query is skipped
  const [executeLazyQuery] = useLazyQuery<
    { getMDMClientConfigForClient: ClientMDMConfig },
    GetMDMClientConfigForClientVariables
  >(GET_MDM_CLIENT_CONFIG_FOR_CLIENT, {
    variables: {
      clientId: effectiveClientId
    },
    fetchPolicy: 'no-cache',
    client: RMMClient
  });

  // Create a custom refetch function that works even when query was initially skipped
  const refetch = useCallback(() => {
    if (shouldSkipQuery) {
      // If query was skipped due to missing clientId, and we still don't have clientId,
      // don't attempt to refetch
      if (!clientId || clientId.trim() === '') {
        return Promise.resolve();
      }
      // Use lazy query to execute the query for the first time
      return executeLazyQuery({
        variables: {
          clientId: effectiveClientId
        }
      });
    } else {
      // Use original refetch if query wasn't skipped
      return originalRefetch();
    }
  }, [
    shouldSkipQuery,
    clientId,
    effectiveClientId,
    executeLazyQuery,
    originalRefetch
  ]);

  const clientConfig = data?.getMDMClientConfigForClient;
  const hasAndroidConfig = !!clientConfig?.androidMDMClientConfig?.id;
  const hasIosConfig = !!clientConfig?.appleMDMClientConfig?.id;

  return {
    clientConfig,
    hasAndroidConfig,
    hasIosConfig,
    loading,
    error,
    refetch
  };
};
